import React, { useState, useEffect } from 'react';
import { X, User, Building2, CreditCard, Shield, Edit, UserCheck, UserX, Eye, Download, Calendar, TrendingUp, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { Employee } from '../../types';
import { useEmployeeTransactions } from '../../hooks';
import { customerApiService } from '../../services/api';

interface EmployeeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
  onEdit: (employee: Employee) => void;
  onSuspend: (employeeId: string) => void;
  onActivate: (employeeId: string) => void;
}

const EmployeeDetailsModal: React.FC<EmployeeDetailsModalProps> = ({
  isOpen,
  onClose,
  employee,
  onEdit,
  onSuspend,
  onActivate
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'kyc'>('overview');
  const { transactions, loading: transactionsLoading, fetchTransactions } = useEmployeeTransactions(employee?.id || '');
  const [photoVerificationData, setPhotoVerificationData] = useState<any>(null);

  useEffect(() => {
    if (employee && activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [employee, activeTab, fetchTransactions]);

  if (!isOpen || !employee) return null;

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Active' },
      suspended: { color: 'bg-red-100 text-red-800', text: 'Suspended' },
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getKycStatusBadge = (kycStatus: string) => {
    const kycConfig = {
      verified: { color: 'bg-green-100 text-green-800', text: 'Verified' },
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' },
    };
    
    const config = kycConfig[kycStatus as keyof typeof kycConfig] || kycConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const creditUtilization = employee.creditLimit > 0 ? (employee.creditUsed / employee.creditLimit) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <User className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{employee.name}</h2>
              <p className="text-sm text-gray-600">Employee ID: {employee.employeeId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: User },
              { id: 'transactions', name: 'Transactions', icon: TrendingUp },
              { id: 'kyc', name: 'KYC Status', icon: Shield },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => onEdit(employee)}
                  className="btn-secondary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Employee
                </button>
                
                {employee.status === 'active' ? (
                  <button
                    onClick={() => onSuspend(employee.id)}
                    className="btn-danger"
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    Suspend
                  </button>
                ) : (
                  <button
                    onClick={() => onActivate(employee.id)}
                    className="btn-success"
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Activate
                  </button>
                )}
              </div>

              {/* Employee Information Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-primary-600" />
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Name:</span>
                      <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Employee ID:</span>
                      <span className="text-sm font-medium text-gray-900">{employee.employeeId}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Department:</span>
                      <span className="text-sm font-medium text-gray-900">{employee.department}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Grade:</span>
                      <span className="text-sm font-medium text-gray-900">{employee.grade}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Join Date:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(employee.joinDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-primary-600" />
                    Status & Verification
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Account Status:</span>
                      {getStatusBadge(employee.status)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">KYC Status:</span>
                      {getKycStatusBadge(employee.kycStatus)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
                  Financial Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Wallet Balance</p>
                    <p className="text-2xl font-bold text-gray-900">₦{employee.walletBalance.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Credit Limit</p>
                    <p className="text-2xl font-bold text-gray-900">₦{employee.creditLimit.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Credit Used</p>
                    <p className="text-2xl font-bold text-gray-900">₦{employee.creditUsed.toLocaleString()}</p>
                  </div>
                </div>
                
                {/* Credit Utilization Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Credit Utilization</span>
                    <span className="text-sm font-medium text-gray-900">{creditUtilization.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        creditUtilization > 80 ? 'bg-red-500' :
                        creditUtilization > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(creditUtilization, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Transactions</h3>
                <button className="btn-secondary">
                  <Download className="h-4 w-4 mr-2" />
                  Export Transactions
                </button>
              </div>
              
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No transactions found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{transaction.category}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{transaction.description}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            ₦{transaction.amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.status === 'SUCCESS' || transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transaction.status === 'FAILED' || transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'kyc' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">KYC Verification Status</h3>
              </div>
              
              <div className="space-y-6">
                {/* Photo Verification Section */}
                <div className="card">
                  <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-primary-600" />
                    Photo Verification
                  </h4>
                  
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      {employee.profileImage ? (
                        <img
                          src={employee.profileImage}
                          alt={employee.name}
                          className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-200">
                          <User className="h-16 w-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Verification Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employee.kycStatus === 'VERIFIED' || employee.kycStatus === 'verified' 
                            ? 'bg-green-100 text-green-800' 
                            : employee.kycStatus === 'REJECTED' || employee.kycStatus === 'rejected'
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {employee.kycStatus}
                        </span>
                      </div>
                      
                      {(employee.kycStatus === 'VERIFIED' || employee.kycStatus === 'verified') && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <p className="text-sm font-medium text-green-900">Identity Verified</p>
                          </div>
                          <p className="text-xs text-green-700 mt-1">
                            All verification steps completed successfully
                          </p>
                        </div>
                      )}

                      {(employee.kycStatus === 'PENDING' || employee.kycStatus === 'pending') && (
                        <div className="p-3 bg-yellow-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-5 w-5 text-yellow-600" />
                            <p className="text-sm font-medium text-yellow-900">Verification Pending</p>
                          </div>
                          <p className="text-xs text-yellow-700 mt-1">
                            Waiting for verification to complete
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* KYC Details */}
                <div className="card">
                  <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-primary-600" />
                    Verification Details
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Employee ID</p>
                      <p className="text-sm font-medium text-gray-900">{employee.employeeId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-sm font-medium text-gray-900">{employee.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Date of Birth</p>
                      <p className="text-sm font-medium text-gray-900">
                        {employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Join Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(employee.joinDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailsModal;
